/**
 * Legs that stand ON the ground (ROUND7 §3). The upright gaits get away with swinging the thighs about a
 * straight-legged pelvis, but a crouch or a lunge drops the hips a third of a metre: swung the same way, the
 * feet would sink into the ground or hang above it. These solve the knee instead - the thigh is placed (or the
 * sole is), and the knee bends exactly as far as puts the sole of the boot on the ground under the pelvis.
 *
 * Angles are about the bone's local x, as every standing pose writes them: a thigh at `-a` is swung `a`
 * forward of hanging straight down; a shin's rotation is relative to its thigh, positive bending the knee.
 */

import type { BoneOf } from './poseBase';

/** Hip to knee (rig.ts REST: the shin hangs 0.42 below its thigh), metres. */
export const THIGH = 0.42;
/** Knee to the sole of the boot (model.ts: the boot's box starts 0.46 down the shin), metres. */
export const SHIN = 0.46;

const clamp1 = (v: number): number => Math.max(-1, Math.min(1, v));

/**
 * Swing the thigh `a` forward of straight down and bend the knee until the sole rests `lift` above the
 * ground, under a pelvis `pelvisY` high. Out of reach (a thigh swung too far for so low a pelvis) the shin
 * hangs straight and the foot is a hair off the ground - the heel-strike, which lasts a pose step at most.
 */
export function plantLeg(bone: BoneOf, thigh: number, shin: number, a: number, pelvisY: number, lift = 0): void {
  const back = Math.acos(clamp1((pelvisY - lift - THIGH * Math.cos(a)) / SHIN));
  bone(thigh).rotation.x = -a;
  bone(shin).rotation.x = a + back;
}

/**
 * Put the sole on the ground `dz` ahead of the hip (negative: behind it) under a pelvis `pelvisY` high,
 * solving both joints; the knee bends forward, as knees do. Out of reach the leg points straight at the spot.
 */
export function reachLeg(bone: BoneOf, thigh: number, shin: number, pelvisY: number, dz: number): void {
  const d = Math.min(Math.hypot(dz, pelvisY), THIGH + SHIN - 1e-6);
  const line = Math.atan2(dz, pelvisY);
  const hip = Math.acos(clamp1((THIGH * THIGH + d * d - SHIN * SHIN) / (2 * THIGH * d)));
  const knee = Math.PI - Math.acos(clamp1((THIGH * THIGH + SHIN * SHIN - d * d) / (2 * THIGH * SHIN)));
  bone(thigh).rotation.x = -(line + hip);
  bone(shin).rotation.x = knee;
}
